import { LGraphNode, LiteGraph } from "litegraph.js";
import { resetAnimationState } from "../effects/GridAnimatorEffects.js";

/**
 * FontExportNode
 * ---------------
 * Captures the grid-animated state for every printable character,
 * traces each frame to SVG paths, and exports a font file (OTF + WOFF)
 * as a ZIP download.
 *
 * Connect the GridAnimator (or any Canvas output) here — the full
 * animation effect IS what gets traced into the font glyphs.
 *
 * Between each character the node:
 *   1. Sets the new character on the TextInputNode
 *   2. Resets the grid animation state (clears cell buffers)
 *   3. Resets the TimingFunction so the animation plays from 0
 *   4. Waits for a configurable number of frames to let the
 *      animation reach the desired capture point
 *   5. Snapshots the canvas and traces it to a glyph
 */

const BASE_FONT_URL =
  "https://danielpow.github.io/futures-branding/build/fonts/Az-(urio)_Hairline.otf";

export class FontExportNode extends LGraphNode {
  constructor() {
    super();
    this.title = "Font Export";

    this.properties = {
      charset: "full",
      fontName: "Cranny-export",
      captureDelay: 30,  // frames to wait per character (higher = more animation)
    };

    // State
    this._status = "Ready";
    this._progress = 0;
    this._totalChars = 0;
    this._exporting = false;
    this._baseFont = null;
    this._baseFontLoading = false;
    this._lastCanvas = null;
    this._lastExportBlob = null;
    this._lastExportName = null;

    // Inputs
    this.addInput("Canvas", "canvas");

    // Widgets
    this.addWidget("combo", "Charset", this.properties.charset, (val) => {
      this.properties.charset = val;
    }, { values: ["full", "uppercase", "lowercase", "digits"] });

    this.addWidget("text", "Font Name", this.properties.fontName, (val) => {
      this.properties.fontName = val || "Cranny-export";
    });

    this.addWidget("slider", "Capture Delay", this.properties.captureDelay, (val) => {
      this.properties.captureDelay = Math.max(5, Math.min(120, Math.round(val)));
    }, { min: 5, max: 120, step: 5 });

    this._exportBtn = this.addWidget("button", "Export Font", null, () => {
      this._startExport();
    });

    this._downloadBtn = this.addWidget("button", "Download", null, () => {
      if (this._lastExportBlob) {
        this._download(this._lastExportBlob, this._lastExportName);
      }
    });
    this._downloadBtn.disabled = true;

    this.size = [220, 230];
    this.mode = LiteGraph.ALWAYS;
    this.color = "#2a2a2a";
    this.bgcolor = "#333333";
  }

  _getCharset() {
    const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const lower = "abcdefghijklmnopqrstuvwxyz";
    const digits = "0123456789";
    const symbols = "!@#$%^&*()-_=+[]{}|;:'\",.<>/?`~";
    switch (this.properties.charset) {
      case "uppercase": return upper;
      case "lowercase": return lower;
      case "digits": return digits;
      default: return upper + lower + digits + symbols;
    }
  }

  async _loadBaseFont() {
    if (this._baseFont) return this._baseFont;
    if (this._baseFontLoading) return null;

    this._baseFontLoading = true;
    this._status = "Loading base font...";

    const opentype = (await import("opentype.js")).default;

    return new Promise((resolve, reject) => {
      opentype.load(BASE_FONT_URL, (err, font) => {
        this._baseFontLoading = false;
        if (err) {
          this._status = "Font load failed!";
          reject(err);
        } else {
          this._baseFont = font;
          this._status = "Base font loaded";
          resolve(font);
        }
      });
    });
  }

  /**
   * Wait for N frames to render, then snapshot the current canvas.
   * This gives the full pipeline (TextRender → Bounce → GridAnimator)
   * time to re-render with the new character.
   */
  _waitForFrames(count) {
    return new Promise((resolve) => {
      let remaining = count;
      const tick = () => {
        remaining--;
        if (remaining <= 0 && this._lastCanvas) {
          const src = this._lastCanvas;
          const copy = document.createElement("canvas");
          copy.width = src.width;
          copy.height = src.height;
          copy.getContext("2d").drawImage(src, 0, 0);
          resolve(copy);
        } else {
          requestAnimationFrame(tick);
        }
      };
      requestAnimationFrame(tick);
    });
  }

  async _startExport() {
    if (this._exporting) return;

    if (!this._lastCanvas) {
      this._status = "No canvas input!";
      return;
    }

    const graph = this.graph;
    if (!graph) return;

    // Find TextInputNode
    const textNode = graph._nodes.find((n) => n.type === "input/TextInput");
    if (!textNode) {
      this._status = "No TextInput node found!";
      return;
    }

    // Find TimingFunctionNode (optional — used to reset animation per glyph)
    const timingNode = graph._nodes.find((n) => n.type === "custom/TimingFunction");

    // Find GridAnimatorNode (to know the cache id to reset)
    const gridNode = graph._nodes.find((n) => n.type === "custom/GridAnimator" || n.type === "display/GridAnimatorNode");

    // Load base font
    let baseFont;
    try {
      baseFont = await this._loadBaseFont();
    } catch (e) {
      this._status = "Base font failed!";
      return;
    }
    if (!baseFont) return;

    this._exporting = true;
    this._lastExportBlob = null;
    this._downloadBtn.disabled = true;

    const chars = this._getCharset();
    this._totalChars = chars.length;
    this._progress = 0;
    this._status = `Exporting 0/${this._totalChars}`;

    // Save original state
    const savedText = textNode.properties.text;
    let prevTimingState = null;
    if (timingNode) {
      prevTimingState = {
        duration: timingNode.properties.duration,
        loop: timingNode.properties.loop,
        play: timingNode.properties.play,
      };
    }

    // Lazy-load heavy deps
    const opentype = (await import("opentype.js")).default;
    const ImageTracer = (await import("imagetracerjs")).default;
    const svgPathParser = await import("svg-path-parser");
    const parseSVG = svgPathParser.parseSVG || (svgPathParser.default && svgPathParser.default.parseSVG);

    const glyphs = [];
    const delay = Math.max(10, this.properties.captureDelay || 30);

    for (let i = 0; i < chars.length; i++) {
      const char = chars[i];

      // 1. Set the new character
      textNode.properties.text = char;

      // 2. Reset grid animation state — flush all cached cells
      //    so no remnants from the previous letter remain
      resetAnimationState("main");

      // 3. Reset timing so animation plays from the start for this glyph
      //    TimingFunctionNode uses _hasStarted/_startTime/_pausedTime instance vars
      if (timingNode) {
        timingNode._hasStarted = false;
        timingNode._startTime = 0;
        timingNode._pausedTime = 0;
        timingNode.properties.duration = prevTimingState
          ? prevTimingState.duration : 1000;
        timingNode.properties.loop = false;
        timingNode.properties.play = true;
      }

      // Also reset GridAnimatorNode's internal timer
      if (gridNode) {
        gridNode.properties.hasStarted = false;
        gridNode.properties.startTime = 0;
      }

      // 4. Wait for the animation to play out
      const canvasSnapshot = await this._waitForFrames(delay);

      // 5. Trace glyph
      const trimmed = this._trimCanvas(canvasSnapshot);
      if (!trimmed) {
        console.warn(`[FontExport] '${char}' — trim returned null (empty canvas)`);
        this._progress = i + 1;
        this._status = `Exporting ${this._progress}/${this._totalChars} (${char})`;
        continue;
      }

      console.log(`[FontExport] '${char}' — trimmed to ${trimmed.width}x${trimmed.height}`);

      try {
        const glyph = await this._canvasToGlyph(
          trimmed, char.charCodeAt(0), opentype, ImageTracer, parseSVG
        );
        if (glyph) {
          glyphs.push(glyph);
          console.log(`[FontExport] '${char}' — glyph OK`);
        } else {
          console.warn(`[FontExport] '${char}' — canvasToGlyph returned null`);
        }
      } catch (e) {
        console.warn(`[FontExport] '${char}' — glyph failed:`, e);
      }

      this._progress = i + 1;
      this._status = `Exporting ${this._progress}/${this._totalChars} (${char})`;
    }

    // Restore original state
    textNode.properties.text = savedText;
    if (timingNode && prevTimingState) {
      Object.assign(timingNode.properties, prevTimingState);
      timingNode._hasStarted = false;
      timingNode._startTime = 0;
      timingNode._pausedTime = 0;
    }
    resetAnimationState("main");

    // Build font and download
    console.log(`[FontExport] Captured ${glyphs.length} glyphs, building font...`);
    try {
      await this._buildAndDownloadFont(glyphs, opentype);
    } catch (e) {
      console.error("[FontExport] Build failed:", e);
      this._status = `Build error: ${e.message}`;
    }

    this._exporting = false;
    if (!this._lastExportBlob) {
      this._status = glyphs.length > 0
        ? `Build failed — ${glyphs.length} glyphs captured but font generation errored`
        : "No glyphs captured!";
    } else {
      this._status = `Done! ${glyphs.length} glyphs`;
    }
  }

  _trimCanvas(sourceCanvas) {
    const ctx = sourceCanvas.getContext("2d", { willReadFrequently: true });
    const { width, height } = sourceCanvas;
    let imageData;
    try {
      imageData = ctx.getImageData(0, 0, width, height);
    } catch (e) {
      return null;
    }
    const pixels = imageData.data;

    let minX = width, maxX = 0, minY = height, maxY = 0;
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (pixels[(y * width + x) * 4 + 3] > 0) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }

    if (maxX < minX || maxY < minY) return null;

    const tw = maxX - minX + 1;
    const th = maxY - minY + 1;
    const c = document.createElement("canvas");
    c.width = tw;
    c.height = th;
    const cx = c.getContext("2d");
    cx.putImageData(ctx.getImageData(minX, minY, tw, th), 0, 0);
    return c;
  }

  _canvasToSVGPath(canvas, ImageTracer) {
    return new Promise((resolve, reject) => {
      const dataURL = canvas.toDataURL("image/png");
      ImageTracer.imageToSVG(
        dataURL,
        (svgString) => {
          if (!svgString) return reject(new Error("SVG generation failed"));
          const parser = new DOMParser();
          const doc = parser.parseFromString(svgString, "image/svg+xml");
          const paths = doc.querySelectorAll("path");
          if (paths.length === 0) return reject(new Error("No paths in SVG"));
          let combined = "";
          paths.forEach((p) => {
            if (p.getAttribute("opacity") !== "0") {
              combined += p.getAttribute("d") + " ";
            }
          });
          resolve(combined);
        },
        { pathomit: 50, strokewidth: 1.5 }
      );
    });
  }

  async _canvasToGlyph(canvas, charCode, opentype, ImageTracer, parseSVG) {
    const pathData = await this._canvasToSVGPath(canvas, ImageTracer);
    const parsed = parseSVG(pathData);
    const path = new opentype.Path();

    const bf = this._baseFont;
    const refGlyph = bf.charToGlyph(String.fromCharCode(charCode));
    const refBox = refGlyph.getBoundingBox();
    const refHeight = refBox.y2 - refBox.y1;
    const refBaselineOffset = refBox.y1;

    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    parsed.forEach((cmd) => {
      if (cmd.x !== undefined) { minX = Math.min(minX, cmd.x); maxX = Math.max(maxX, cmd.x); }
      if (cmd.y !== undefined) { minY = Math.min(minY, cmd.y); maxY = Math.max(maxY, cmd.y); }
    });

    const gh = maxY - minY;
    const scale = refHeight / gh;
    const xOff = ((refBox.x1 + refBox.x2) / 2) - ((minX + maxX) / 2) * scale;
    let yOff = gh * scale + refBaselineOffset - minY * scale;
    if (refBaselineOffset < bf.descender) {
      yOff += bf.descender - refBaselineOffset;
    }

    parsed.forEach((cmd) => {
      switch (cmd.code) {
        case "M": path.moveTo(cmd.x * scale + xOff, cmd.y * -scale + yOff); break;
        case "L": path.lineTo(cmd.x * scale + xOff, cmd.y * -scale + yOff); break;
        case "C":
          path.bezierCurveTo(
            cmd.x1 * scale + xOff, cmd.y1 * -scale + yOff,
            cmd.x2 * scale + xOff, cmd.y2 * -scale + yOff,
            cmd.x * scale + xOff, cmd.y * -scale + yOff
          );
          break;
        case "Q":
          path.quadraticCurveTo(
            cmd.x1 * scale + xOff, cmd.y1 * -scale + yOff,
            cmd.x * scale + xOff, cmd.y * -scale + yOff
          );
          break;
        case "Z": path.closePath(); break;
      }
    });

    return new opentype.Glyph({
      name: String.fromCharCode(charCode),
      unicode: charCode,
      advanceWidth: refGlyph.advanceWidth,
      path,
    });
  }

  async _buildAndDownloadFont(glyphs, opentype) {
    if (glyphs.length === 0) {
      this._status = "No glyphs captured!";
      return;
    }

    this._status = "Building font...";
    console.log(`[FontExport] Building font with ${glyphs.length} glyphs`);

    const bf = this._baseFont;
    // Clone base glyphs array so we don't mutate the original
    const baseGlyphs = [];
    const srcGlyphs = bf.glyphs.glyphs;
    for (const key in srcGlyphs) {
      if (srcGlyphs.hasOwnProperty(key)) {
        baseGlyphs.push(srcGlyphs[key]);
      }
    }

    const name = `${this.properties.fontName}-${Date.now()}`;
    console.log(`[FontExport] Font name: ${name}, base glyphs: ${baseGlyphs.length}`);

    const font = new opentype.Font({
      familyName: name,
      styleName: "Regular",
      unitsPerEm: bf.unitsPerEm,
      ascender: bf.ascender,
      descender: bf.descender,
      glyphs: baseGlyphs,
    });

    console.log(`[FontExport] Font created, merging new glyphs...`);

    // Merge new glyphs
    glyphs.forEach((g) => {
      const existingIdx = baseGlyphs.findIndex((bg) => bg.unicode === g.unicode);
      if (existingIdx !== -1) {
        font.glyphs.glyphs[existingIdx] = g;
      } else {
        font.glyphs.push(g);
      }
    });

    // Generate OTF buffer
    console.log(`[FontExport] Generating OTF...`);
    const otfBuffer = font.toArrayBuffer();
    console.log(`[FontExport] OTF buffer size: ${otfBuffer.byteLength}`);

    // Just download the OTF directly — no zip needed for single file
    const otfBlob = new Blob([otfBuffer], { type: "font/otf" });

    // Store for re-download
    this._lastExportBlob = otfBlob;
    this._lastExportName = `${name}.otf`;
    this._downloadBtn.disabled = false;

    console.log(`[FontExport] Downloading ${name}.otf (${otfBlob.size} bytes)`);

    // Auto-download
    this._download(otfBlob, `${name}.otf`);
  }

  _download(blob, filename) {
    try {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.style.display = "none";
      document.body.appendChild(a);
      // Use timeout to ensure the click registers
      setTimeout(() => {
        a.click();
        console.log(`[FontExport] Download triggered: ${filename}`);
        setTimeout(() => {
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }, 5000);
      }, 100);
    } catch (e) {
      console.error("[FontExport] Download failed:", e);
      this._status = `Download error: ${e.message}`;
    }
  }

  onDrawForeground(ctx) {
    const w = this.size[0];
    const h = this.size[1];

    // Progress bar during export
    if (this._exporting && this._totalChars > 0) {
      const barY = h - 45;
      const barH = 8;
      const pct = this._progress / this._totalChars;

      ctx.fillStyle = "rgba(0,0,0,0.3)";
      ctx.fillRect(10, barY, w - 20, barH);
      ctx.strokeStyle = "rgba(255,255,255,0.1)";
      ctx.strokeRect(10, barY, w - 20, barH);

      ctx.fillStyle = "#a855f7";
      ctx.fillRect(10, barY, (w - 20) * pct, barH);

      ctx.fillStyle = "#fff";
      ctx.font = "bold 9px sans-serif";
      ctx.fillText(`${Math.round(pct * 100)}%`, w / 2 - 10, barY + 7);
    }

    // Download ready indicator
    if (this._lastExportBlob && !this._exporting) {
      ctx.fillStyle = "#22c55e";
      ctx.beginPath();
      ctx.arc(w - 15, h - 60, 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.font = "9px sans-serif";
      ctx.fillText("Ready to download", 10, h - 56);
    }

    // Status
    ctx.fillStyle = this._exporting ? "#a855f7" : "rgba(255,255,255,0.6)";
    ctx.font = "10px sans-serif";
    ctx.fillText(this._status, 10, h - 12);

    // Charset preview
    const chars = this._getCharset();
    ctx.fillStyle = "rgba(255,255,255,0.25)";
    ctx.font = "8px monospace";
    const preview = chars.length > 35 ? chars.slice(0, 35) + "…" : chars;
    ctx.fillText(preview, 10, h - 25);

  }

  onExecute() {
    const input = this.getInputData(0);
    if (input) {
      this._lastCanvas = input;
    }
  }
}

LiteGraph.registerNodeType("export/FontExport", FontExportNode);
